import { computed, type MaybeRefOrGetter } from 'vue';
import { useElementBounding } from '@vueuse/core';
import { useLayoutStore } from '@/stores/layoutStore.ts';
import { storeToRefs } from 'pinia';

type MaybeEl = HTMLElement | null | undefined;

type UseRemainingViewportOptions = {
  /** Minimum height to return. Default: 400 */
  minHeight?: number;
};

/**
 * Computes the available space below `target` until the container's bottom edge.
 * If no container is provided, uses the viewport.
 */
export function useRemainingViewportHeight(
  target: MaybeRefOrGetter<MaybeEl>,
  options: UseRemainingViewportOptions = {},
) {
  const { minHeight = 200 } = options;

  const layoutStore = useLayoutStore();
  const { pageElement } = storeToRefs(layoutStore);

  // element bounding for both target and page elements in component context
  const targetBounding = useElementBounding(target, {
    immediate: true,
  });

  const pageBounding = useElementBounding(pageElement, {
    immediate: true,
  });

  const availableViewportHeight = computed(() => {
    const height = pageBounding.height.value - targetBounding.top.value;
    return height < minHeight ? minHeight : height;
  });

  return { height: availableViewportHeight };
}
